import { createRef, useCallback, useEffect, useMemo } from 'react';

import { Id } from 'react-toastify';
import { NotificationCenterItem, useNotificationCenter } from 'react-toastify/addons/use-notification-center';

import { parseISO } from 'date-fns';

import { useClickOutside, useToggle } from '@hsp/react-hooks';
import { containsElement, isDefined, isNotDefined, isNotEmpty, MILLISECOND } from '@hsp/utilities';

import { useBroadcasts } from '@c/notifications/hooks/broadcasts';
import { INotification } from '@c/notifications/types';

import { useActions } from '@h/actions';

import { useFetchNotifications } from 'components/notifications/hooks/queries';

export const useNotificationCenterIconConfiguration = () => {
  const [isOpen, toggleIsOpen, setIsOpen] = useToggle(false);

  useBroadcasts();

  const { add, cleanup } = useActions();
  const { data } = useFetchNotifications();
  const notificationCenter = useNotificationCenter<INotification>();

  const iconRef = createRef<HTMLDivElement>();
  const panelRef = createRef<HTMLDivElement>();

  const unreadCount = useMemo(() => notificationCenter.unreadCount, [notificationCenter.unreadCount]);

  const handleClick = useCallback(toggleIsOpen, [toggleIsOpen]);

  const handleRemove = useCallback(
    (id: Id) => {
      notificationCenter.remove(id);
    },
    [notificationCenter]
  );

  useClickOutside(panelRef, (e) => {
    if (containsElement(iconRef.current, e.target as HTMLElement)) {
      return;
    }

    setIsOpen(false);
  });

  useEffect(() => {
    if (isNotEmpty(data)) {
      for (const notification of data ?? []) {
        const { content: _, createdDate, id, readDate } = notification;

        if (isNotDefined(createdDate) || isNotDefined(id)) {
          continue;
        }

        const createdAt = new Date(parseISO(createdDate).getTime() - new Date().getTimezoneOffset() * 60 * MILLISECOND);
        const item: NotificationCenterItem<INotification> = {
          createdAt: createdAt.getTime(),
          data: notification,
          id,
          read: isDefined(readDate),
        };

        notificationCenter.add(item);
      }
    }
  }, [data, notificationCenter]);

  useEffect(() => {
    add('remove-notification', handleRemove);

    return () => cleanup('remove-notification');
  }, [add, cleanup, handleRemove]);

  return {
    handleClick,
    iconRef,
    isOpen,
    notificationCenter,
    panelRef,
    unreadCount,
  };
};
